import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AccountLinks from "@/components/AccountLinks";
import {
  Building2,
  Plus,
  Pencil,
  Trash2,
  Check,
  Loader2,
  Mail,
  Phone,
  User,
} from "lucide-react";

const empty = { name: "", owner: "", contact_email: "", phone: "" };

export default function VetFarms() {
  const { activeFarm, setActiveFarm, refreshInvites } = useAuth();
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // farm id or null (create)
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: ws }] = await Promise.all([
        api.get("/vet/farms"),
        api.get("/vet/workspaces"),
      ]);
      setFarms(data);
      // If active farm was deleted/unlinked elsewhere, clear it (check all workspaces).
      if (activeFarm && !ws.find((f) => f.id === activeFarm.id)) {
        setActiveFarm(null);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load farms");
    } finally {
      setLoading(false);
    }
  }, [activeFarm, setActiveFarm]);

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setDialogOpen(true);
  };

  const openEdit = (farm) => {
    setEditing(farm.id);
    setForm({
      name: farm.name || "",
      owner: farm.owner || "",
      contact_email: farm.contact_email || "",
      phone: farm.phone || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() && !form.owner.trim()) {
      toast.error("Enter a farm name or an owner name");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/vet/farms/${editing}`, form);
        if (activeFarm?.id === data.id) setActiveFarm(data);
        toast.success("Farm updated");
      } else {
        const { data } = await api.post("/vet/farms", form);
        toast.success("Farm added");
        if (!activeFarm) setActiveFarm(data);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (farm) => {
    if (!window.confirm(`Delete "${farm.name}" and all of its mares, foals and records? This cannot be undone.`)) return;
    try {
      await api.delete(`/vet/farms/${farm.id}`);
      if (activeFarm?.id === farm.id) setActiveFarm(null);
      toast.success("Farm deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const makeActive = (farm) => {
    setActiveFarm(farm);
    toast.success(`Now working in ${farm.name}`);
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Client Farms</h1>
          <p className="text-muted-foreground text-sm">
            Each farm keeps its own private mares, teasing charts, foals & records. Pick one to work in it.
          </p>
        </div>
        <Button onClick={openCreate} className="gap-2" data-testid="add-farm-btn">
          <Plus className="h-4 w-4" /> Add farm
        </Button>
      </div>

      {/* Link to existing farm accounts */}
      <div className="mb-6">
        <AccountLinks role="vet" onChange={refreshInvites} />
      </div>

      <div className="mb-3">
        <h2 className="font-bold text-lg">Farms you manage yourself</h2>
        <p className="text-sm text-muted-foreground">Records you keep on behalf of a farm that doesn&apos;t have its own account.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : farms.length === 0 ? (
        <div className="text-center py-20 bg-card border border-dashed border-border rounded-xl" data-testid="farms-empty">
          <Building2 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-lg font-semibold">No farms yet</p>
          <p className="text-muted-foreground text-sm mt-1 mb-5">Add your first client farm to start tracking its mares.</p>
          <Button onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" /> Add farm
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {farms.map((f) => {
            const isActive = activeFarm?.id === f.id;
            return (
              <div
                key={f.id}
                data-testid={`farm-card-${f.id}`}
                className={`rounded-2xl border bg-card p-5 transition-shadow hover:shadow-lg ${
                  isActive ? "border-primary ring-1 ring-primary" : "border-border"
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  {isActive && (
                    <span className="text-xs font-bold uppercase tracking-wide text-primary bg-primary/10 rounded-full px-2.5 py-1" data-testid={`farm-active-badge-${f.id}`}>
                      Active
                    </span>
                  )}
                </div>
                <h3 className="font-bold text-lg leading-tight">{f.name || f.owner}</h3>
                <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                  {f.owner && f.name && (
                    <p className="flex items-center gap-2"><User className="h-3.5 w-3.5" /> {f.owner}</p>
                  )}
                  {f.contact_email && (
                    <p className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 shrink-0" /> {f.contact_email}</p>
                  )}
                  {f.phone && (
                    <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {f.phone}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-4">
                  {isActive ? (
                    <Button variant="secondary" size="sm" className="flex-1 gap-1.5" disabled data-testid={`farm-active-${f.id}`}>
                      <Check className="h-4 w-4" /> Working here
                    </Button>
                  ) : (
                    <Button size="sm" className="flex-1 gap-1.5" onClick={() => makeActive(f)} data-testid={`farm-select-${f.id}`}>
                      Open farm
                    </Button>
                  )}
                  <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => openEdit(f)} data-testid={`farm-edit-${f.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="icon" className="h-9 w-9 text-destructive" onClick={() => remove(f)} data-testid={`farm-delete-${f.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit farm" : "Add farm"}</DialogTitle>
            <DialogDescription>
              {editing ? "Update this farm's details." : "Create a new client farm with its own records."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <p className="text-xs text-muted-foreground">Enter a <strong>farm name</strong> or an <strong>owner name</strong> (at least one is required).</p>
            <div className="space-y-1.5">
              <Label htmlFor="farm-name">Farm name</Label>
              <Input id="farm-name" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="The Barn" data-testid="farm-input-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="farm-owner">Owner name</Label>
              <Input id="farm-owner" value={form.owner} onChange={(e) => setForm((s) => ({ ...s, owner: e.target.value }))} placeholder="Jane Doe" data-testid="farm-input-owner" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="farm-email">Contact email</Label>
              <Input id="farm-email" type="email" value={form.contact_email} onChange={(e) => setForm((s) => ({ ...s, contact_email: e.target.value }))} placeholder="owner@farm.com" data-testid="farm-input-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="farm-phone">Phone <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input id="farm-phone" value={form.phone} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))} placeholder="(555) 123-4567" data-testid="farm-input-phone" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="farm-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {editing ? "Save" : "Add farm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
